import _ from 'lodash';
import * as StringUtils from '@packages/utilities/string-utils';
import {
  splitCssValue,
  stripVendorPrefix,
} from '@packages/systems/core/utils/CssStringUtils';
import * as CssUtils from '@packages/systems/style-legacy/utils';

import type {CssProperty} from '@packages/systems/core/utils/CssStringUtils';

const getValue = _.property('value');

/**
 * Collapses a set of properties to shorthand notation.
 * @param  {Object}   properties    A property -> value map.
 * @return {Object}                 A property -> value map with styles collapsed into shorthand.
 */
export function compactCssPropertyList(
  properties: CssProperty[]
): CssProperty[] {
  properties = collapseMargin(properties);
  properties = collapsePadding(properties);
  properties = collapseBorderRadius(properties);
  properties = collapseBorder(properties);
  properties = collapseTransition(properties);
  properties = collapseColumnRule(properties);
  properties = collapseFlex(properties);

  // Fix webkit gradients
  properties.forEach((prop) => {
    const value = prop.value;
    if (prop.property === 'background-image' && Array.isArray(value)) {
      value.forEach((subValue: string, index: number) => {
        if (_.startsWith(subValue, '-webkit-')) {
          // Linear gradients should just keep the same values, just strip the prefix
          if (subValue.indexOf('linear-gradient') > 0) {
            value[index] = stripVendorPrefix(subValue);
          }

          // Radial gradients have to be changed to W3C format
          if (subValue.indexOf('radial-gradient') > 0) {
            const unwrapped = CssUtils.unwrapValue(subValue);
            const parts = splitCssValue(unwrapped.inside, ',');
            const inside =
              parts[1] + ' at ' + parts[0] + ', ' + _.tail(parts).join(', ');

            value[index] =
              stripVendorPrefix(unwrapped.outside ?? '') + '(' + inside + ')';
          }
        }
      });
    }
  });

  return properties;
}

export function collapseBorder(properties: CssProperty[]): CssProperty[] {
  const BORDER = 'border';
  const BORDER_PREFIX = BORDER + '-';
  const COLOR = 'color';
  const WIDTH = 'width';
  const STYLE = 'style';
  const BORDER_COLOR = BORDER_PREFIX + COLOR;
  const BORDER_WIDTH = BORDER_PREFIX + WIDTH;
  const BORDER_STYLE = BORDER_PREFIX + STYLE;

  properties = _collapseFourSides(properties, BORDER, COLOR);
  properties = _collapseFourSides(properties, BORDER, WIDTH);
  properties = _collapseFourSides(properties, BORDER, STYLE);

  // Try to collapse into one line shorthand
  const borderProps = _.filter(properties, function (prop) {
    return (
      prop.property === BORDER_STYLE ||
      prop.property === BORDER_WIDTH ||
      prop.property === BORDER_COLOR
    );
  });
  const border = _.fromPairs(
    _.map(borderProps, function (prop) {
      return [StringUtils.strRightBack(prop.property, '-'), prop.value];
    })
  );
  const sides: Record<string, any> = {};

  if (
    border.width !== undefined &&
    border.style !== undefined &&
    border.color !== undefined
  ) {
    // All properties are the same for all sides - combine into simple shorthand
    if (
      border.width.indexOf(' ') < 0 &&
      border.style.indexOf(' ') < 0 &&
      border.color.indexOf(' ') < 0
    ) {
      properties = _.reject<CssProperty>(
        properties,
        function (prop: CssProperty) {
          return (
            prop.property === BORDER_STYLE ||
            prop.property === BORDER_WIDTH ||
            prop.property === BORDER_COLOR
          );
        }
      );
      properties.push({
        property: BORDER,
        value: border.width + ' ' + border.style + ' ' + border.color,
      });
    }
  }

  // Collapse single-side borders into shorthand
  _.each(properties, function (prop) {
    const p = prop.property;

    if (!_.startsWith(p, BORDER_PREFIX) || _.endsWith(p, '-radius')) {
      return;
    }

    const parts = StringUtils.strRight(p, BORDER_PREFIX).split('-');
    if (parts.length !== 2) {
      return;
    }

    const side = parts[0];
    const part = parts[1];

    // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
    sides[side] = sides[side] || {};
    // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
    sides[side][part] = prop.value;
  });

  _.each(['left', 'right', 'top', 'bottom'], function (s) {
    const side = sides[s];
    if (
      side &&
      side.style !== undefined &&
      side.width !== undefined &&
      side.color !== undefined
    ) {
      properties = _.reject(properties, function (prop) {
        return (
          _.startsWith(prop.property, BORDER_PREFIX + s + '-') &&
          !_.endsWith(prop.property, '-radius')
        );
      });
      properties.push({
        property: BORDER_PREFIX + s,
        value: side.width + ' ' + side.style + ' ' + side.color,
      });
    }
  });

  return properties;
}

export function collapseColumnRule(properties: CssProperty[]): CssProperty[] {
  const relevantPropsMap: Record<string, any> = {};
  const relevantPropIndexes: Record<string, any> = {};

  // find relevant column rule props
  properties.forEach(function (property, i) {
    const propName = property.property;
    if (propName.indexOf('column-rule-') > -1) {
      const propSegement = propName.split('-rule-')[1];
      // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
      relevantPropsMap[propSegement] = property.value;
      relevantPropIndexes[i] = 1;
    }
  });

  // only collapse if they're all there (width, style, color)
  if (
    !relevantPropsMap.hasOwnProperty('width') ||
    !relevantPropsMap.hasOwnProperty('style') ||
    !relevantPropsMap.hasOwnProperty('color')
  ) {
    return properties;
  }

  // remove collapsing props
  properties = properties.filter(function (property, i) {
    return !(i in relevantPropIndexes);
  });

  // add our collapsed column-rule
  properties.push({
    property: 'column-rule',
    value:
      relevantPropsMap.width +
      ' ' +
      relevantPropsMap.style +
      ' ' +
      relevantPropsMap.color,
  });

  return properties;
}

export function collapseFlex(properties: CssProperty[]): CssProperty[] {
  const defaultValues = {
    'flex-grow': '1',
    'flex-shrink': '1',
    'flex-basis': '0%',
  } as const;

  const order = {
    'flex-grow': 0,
    'flex-shrink': 1,
    'flex-basis': 2,
  } as const;

  const relevantProps = properties.filter(
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'flex-grow': "1"; readonly 'flex-shrink': "1"; readonly 'flex-basis': "0%"; }'.
    ({property}) => defaultValues[property]
  );

  if (relevantProps.length < 3) {
    return properties;
  }

  const newProps: Array<CssProperty> = _.difference(properties, relevantProps);

  newProps.push({
    property: 'flex',
    value: relevantProps
      .filter(
        ({property, value}, index, array) =>
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'flex-grow': "1"; readonly 'flex-shrink': "1"; readonly 'flex-basis': "0%"; }'.
          value !== defaultValues[property] ||
          (property === 'flex-grow' &&
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            (array[1].value !== defaultValues['flex-shrink'] ||
              // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
              array[2].value === defaultValues['flex-basis']))
      )
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'flex-grow': 0; readonly 'flex-shrink': 1; readonly 'flex-basis': 2; }'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'flex-grow': 0; readonly 'flex-shrink': 1; readonly 'flex-basis': 2; }'.
      .sort((a, b) => order[a.property] - order[b.property])
      .map(getValue)
      .join(' '),
  });

  return newProps;
}

export function collapseTransition(properties: CssProperty[]): CssProperty[] {
  const WEBKIT = '-webkit-';
  const TRANSITION = 'transition';
  const TRANSITION_PREFIX = TRANSITION + '-';

  const relevantProps = _.filter(properties, function (prop) {
    return (
      _.startsWith(prop.property, TRANSITION_PREFIX) ||
      _.startsWith(prop.property, WEBKIT + TRANSITION_PREFIX)
    );
  });

  // Only collapse if all 3 main properties (property, duration, easing) are present
  if (relevantProps.length < 3) {
    return properties;
  }

  // Build a map pointing the transition subvalue (duration, property, timing) to the array of part values
  let partCount = 0;
  const map = _.fromPairs(
    _.map(relevantProps, function (prop) {
      let valueArray;
      if (Array.isArray(prop.value)) {
        valueArray = prop.value;
      } else if (typeof prop.value === 'string') {
        valueArray = splitCssValue(prop.value, ',');
      }

      const subValueKey = CssUtils.getSubvalueKey(prop.property);

      // Don't include delay in the part count for now since Webkit seems to return double the number of entries
      if (subValueKey !== 'delay' && valueArray) {
        partCount = Math.max(partCount, valueArray.length);
      }

      return [subValueKey, valueArray];
    })
  );

  // Don't collapse to shorthand if property is missing
  if (!map.property) {
    return properties;
  }

  // @ts-expect-error - TS7006 - Parameter 'part' implicitly has an 'any' type.
  const isPartCommented = (part) => {
    return _.startsWith(part, '/*') && _.endsWith(part, '*/');
  };
  const combinedValues: Array<string> = [];
  for (let i = 0; i < partCount; i++) {
    let property = stripVendorPrefix(map.property[i]);
    let duration = map.duration[i];
    let timing = map.timing[i];
    const allCommented = _.every([property, duration, timing], isPartCommented);

    if (allCommented) {
      property = _.trim(property, '/\\*, ');
      duration = _.trim(duration, '/\\*, ');
      timing = _.trim(timing, '/\\*, ');
    }

    combinedValues.push(
      (allCommented ? '/* ' : '') +
        property +
        ' ' +
        duration +
        ' ' +
        timing +
        (allCommented ? ' */' : '')
    );
  }

  // Remove old individual props
  properties = _.reject<CssProperty>(properties, function (prop: CssProperty) {
    return (
      _.startsWith(prop.property, TRANSITION_PREFIX) ||
      _.startsWith(prop.property, WEBKIT + TRANSITION_PREFIX)
    );
  });

  // Add in combined prop
  const combinedProp = {
    property: TRANSITION,
    value: combinedValues.length === 1 ? combinedValues[0] : combinedValues,
    separator: combinedValues.length > 1 ? ',' : undefined,
  } as const;

  // @ts-expect-error - TS2345 - Argument of type '{ readonly property: "transition"; readonly value: string | string[] | undefined; readonly separator: "," | undefined; }' is not assignable to parameter of type 'CssProperty'.
  properties.push(combinedProp);

  return properties;
}

export function collapseMargin(properties: CssProperty[]): CssProperty[] {
  return _collapseFourSides(properties, 'margin');
}

export function collapsePadding(properties: CssProperty[]): CssProperty[] {
  return _collapseFourSides(properties, 'padding');
}

type CssBorderRadiusCornerKey =
  | 'topLeft'
  | 'topRight'
  | 'bottomRight'
  | 'bottomLeft';

export function collapseBorderRadius(properties: CssProperty[]): CssProperty[] {
  return _collapseFourSides(properties, 'border', 'radius', [
    'topLeft',
    'topRight',
    'bottomRight',
    'bottomLeft',
  ]);
}

function _collapseFourSides(
  properties: CssProperty[],
  prefix: string,
  postfix?: string | null,
  sides?: CssBorderRadiusCornerKey[] | null
): CssProperty[] {
  const relevantProps = _.filter(properties, function (prop) {
    return (
      _.startsWith(prop.property, prefix + '-') &&
      (postfix ? _.endsWith(prop.property, '-' + postfix) : true)
    );
  });

  const obj = _.fromPairs(
    _.map(relevantProps, function (prop) {
      if (postfix) {
        return [
          StringUtils.camelize(
            StringUtils.strLeft(
              StringUtils.strRight(prop.property, prefix + '-'),
              '-' + postfix
            )
          ),
          prop.value,
        ];
      } else {
        return [
          StringUtils.camelize(StringUtils.strRightBack(prop.property, '-')),
          prop.value,
        ];
      }
    })
  );

  // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
  const top = sides ? obj[sides[0]] : obj.top;
  // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
  const right = sides ? obj[sides[1]] : obj.right;
  // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
  const bottom = sides ? obj[sides[2]] : obj.bottom;
  // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
  const left = sides ? obj[sides[3]] : obj.left;

  if (
    left !== undefined &&
    right !== undefined &&
    top !== undefined &&
    bottom !== undefined
  ) {
    let newValue = null;
    properties = _.reject(properties, function (prop) {
      if (postfix) {
        return (
          _.startsWith(prop.property, prefix + '-') &&
          _.endsWith(prop.property, '-' + postfix)
        );
      }

      return _.startsWith(prop.property, prefix + '-');
    });

    if (left === right && top === bottom && left === top) {
      // All are equal
      newValue = left;
    } else if (left === right && top === bottom && left === right) {
      // Top/bottom, left/right are equal
      newValue = top + ' ' + left;
    } else {
      newValue =
        top + ' ' + right + ' ' + bottom + (left === right ? '' : ' ' + left);
    }

    properties.push({
      property: prefix + (postfix ? '-' + postfix : ''),
      value: newValue,
    });
  }

  return properties;
}
